import { Parcel } from '../types';

// Manager's GeoJSON already uses our exact class IDs! 
// Just need to handle case variations
const CLASS_NAME_MAP: Record<string, string> = {
  // Exact matches (lowercase)
  'cropland': 'crop',
  'forestry': 'forestry',
  'grassland': 'grass',
  'urban': 'urban',
  'water': 'water',
  'wetland': 'wetland',
  
  // Case-insensitive fallbacks
  'crop': 'crop',
  'grass': 'grass',
};

interface GeoJSONFeature {
  type: 'Feature';
  properties: {
    class: number;
    class_name: string;
    color: string;
    area_ha: number;
    chunk_id: number;
  };
  geometry: {
    type: 'Polygon';
    coordinates: number[][][]; // Raw from JSON - arrays of arrays
  };
}

interface GeoJSONData {
  type: 'FeatureCollection';
  features: GeoJSONFeature[];
}

// Normalize EPSG:3035 coordinates to 0-2000 range for SVG display
function normalizeCoordinates(features: GeoJSONFeature[]): {
  normalized: Array<{
    type: 'Feature';
    properties: GeoJSONFeature['properties'];
    geometry: {
      type: 'Polygon';
      coordinates: [number, number][][];
    };
  }>;
  bounds: { minX: number; maxX: number; minY: number; maxY: number };
} {
  let minX = Infinity,
    maxX = -Infinity;
  let minY = Infinity,
    maxY = -Infinity;

  // Find bounds
  features.forEach((feature) => {
    feature.geometry.coordinates[0].forEach((coord) => {
      const x = coord[0];
      const y = coord[1];
      minX = Math.min(minX, x);
      maxX = Math.max(maxX, x);
      minY = Math.min(minY, y);
      maxY = Math.max(maxY, y);
    });
  });

  const width = maxX - minX;
  const height = maxY - minY;
  const scale = 2000 / Math.max(width, height);

  // Normalize to 0-2000 with proper typing
  const normalized = features.map((feature) => ({
    type: 'Feature' as const,
    properties: feature.properties,
    geometry: {
      type: 'Polygon' as const,
      coordinates: [
        feature.geometry.coordinates[0].map((coord): [number, number] => [
          (coord[0] - minX) * scale,
          2000 - (coord[1] - minY) * scale, // Flip Y for SVG
        ]),
      ],
    },
  }));

  return { normalized, bounds: { minX, maxX, minY, maxY } };
}

export function loadGeoJSONParcels(geojsonData: GeoJSONData): Parcel[] {
  const { normalized } = normalizeCoordinates(geojsonData.features);

  return normalized.map((feature, idx) => {
    const className = feature.properties.class_name.toLowerCase();
    const mappedClass = CLASS_NAME_MAP[className] || CLASS_NAME_MAP[feature.properties.class_name] || 'grass';

    // Determine soil zone (left half = organic, right half = mineral)
    const centerX =
      feature.geometry.coordinates[0].reduce((sum, [x]) => sum + x, 0) /
      feature.geometry.coordinates[0].length;
    const soilZone = centerX < 1000 ? 'organic' : 'mineral';

    return {
      id: `p${idx}`,
      cls: mappedClass,
      pts: feature.geometry.coordinates[0],
      areaHa: feature.properties.area_ha,
      soilZone: soilZone as 'organic' | 'mineral',
    };
  });
}